import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { from, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LOCALSTORAGEKEYS } from '../_constants/localStorageKeys';
import { LocalService } from '../_services/_storage/local.service';
import { PermissionsService } from '../_services/_permissions/permissions.service';
import { GeographyService } from '../_services/_geography/geography.service';
import { RoleService } from '../_services/_role/role.service';
import { UserService } from '../_services/_user/user.service';
import { ProfilesService } from '../_modules/inputs/profiles/profiles.service';

@Injectable({
    providedIn: 'root',
})
export class RolesConfigResolverService implements Resolve<any> {
    constructor(
        private keycloakService: KeycloakService,
        private localService: LocalService,
        private permissionsService: PermissionsService,
        private geographyService: GeographyService,
        private roleService: RoleService,
        private userService: UserService,
        private profilesService: ProfilesService
    ) {}

    resolve(): Observable<any> {
        const env = environment as { authMode?: string };
        if (env.authMode === 'mock') {
            const mock = {
                name: 'Demo User',
                email: 'demo@lokos.in',
                preferred_username: 'demo_user',
            };
            this.writeProfile(mock);
            return this.geographyService.initMock().pipe(map(() => mock));
        }
        return from(this.keycloakService.loadUserInfo()).pipe(
            tap((info: any) => this.writeProfile(info)),
            switchMap((info: any) =>
                forkJoin([
                    this.geographyService
                        .init(info?.sub || '', info?.resource_access || {})
                        .pipe(catchError(() => of(null))),
                    this.loadProfileImage(),
                    this.loadUserPermissions(info?.sub || ''),
                ]).pipe(
                    tap(() => this.roleService.init()),
                    switchMap(() =>
                        forkJoin([
                            this.loadModulePermissions(),
                            this.loadUserClf(info),
                        ]).pipe(map(() => info))
                    )
                )
            ),
            catchError(() => of(null))
        );
    }

    private loadUserPermissions(sub: string): Observable<any> {
        if (!sub) {
            this.localService.set(LOCALSTORAGEKEYS.USERPERMISSIONS, []);
            return of(null);
        }
        return this.permissionsService.getUserPermissions(sub).pipe(
            tap((perms: any) =>
                this.localService.set(
                    LOCALSTORAGEKEYS.USERPERMISSIONS,
                    Array.isArray(perms) ? perms : []
                )
            ),
            catchError(() => {
                this.localService.set(LOCALSTORAGEKEYS.USERPERMISSIONS, []);
                return of(null);
            })
        );
    }

    private loadProfileImage(): Observable<any> {
        return this.userService.getUserProfileImage().pipe(
            tap((image: any) => {
                if (image && typeof image === 'string') {
                    this.localService.set(
                        LOCALSTORAGEKEYS.PROFILE_IMAGE,
                        image
                    );
                }
            }),
            catchError(() => of(null))
        );
    }

    private loadUserClf(info: any): Observable<any> {
        if (this.roleService.selectedLevel !== 'clf') {
            return of(null);
        }
        const username = info?.preferred_username || '';
        if (!username) {
            return of(null);
        }
        const cached = this.localService.get(LOCALSTORAGEKEYS.USER_CLF) as {
            sub?: string;
            clfCode?: string;
        } | null;
        if (cached?.clfCode && cached?.sub === info?.sub) {
            return of(cached);
        }
        return this.profilesService.getClfUserMappingByUserId(username).pipe(
            switchMap((resp: any) => {
                const data = resp?.data;
                const record = Array.isArray(data) ? data[0] : null;
                const clfCode = record?.clf_code || '';
                if (!clfCode) {
                    return of(null);
                }
                return this.profilesService
                    .getClfBasicDetailsByCode(clfCode)
                    .pipe(
                        tap((res: any) => {
                            const d = res?.data;
                            this.localService.set(LOCALSTORAGEKEYS.USER_CLF, {
                                sub: info?.sub || '',
                                clfCode,
                                clfName: d?.clf_name || '',
                            });
                        }),
                        catchError(() => {
                            this.localService.set(LOCALSTORAGEKEYS.USER_CLF, {
                                sub: info?.sub || '',
                                clfCode,
                                clfName: '',
                            });
                            return of(null);
                        })
                    );
            }),
            catchError(() => of(null))
        );
    }

    private loadModulePermissions(): Observable<any> {
        const role = this.resolveModuleRole();
        if (!role) {
            return of(null);
        }
        return this.permissionsService.getModulePermissionsByRole(role).pipe(
            tap((res: any) =>
                this.localService.set(
                    LOCALSTORAGEKEYS.MODULEPERMISSIONS,
                    res?.data ?? res ?? null
                )
            ),
            catchError(() => of(null))
        );
    }

    private resolveModuleRole(): string {
        const selected = this.roleService.selectedRole;
        if (!selected) {
            return '';
        }
        if (selected.level === 'clf') {
            return selected.code.includes('MCLF Manager')
                ? 'CLF_MANAGER'
                : 'CLF_ASSISTANT';
        }
        return selected.code.toUpperCase().replace(/-/g, '_');
    }

    private writeProfile(info: any) {
        if (!info) return;
        const existing =
            this.localService.get(LOCALSTORAGEKEYS.USERPROFILE) || {};
        const merged = {
            ...existing,
            ...info,
            preferred_username: (
                info.preferred_username ||
                existing.preferred_username ||
                ''
            )
                .toString()
                .toUpperCase(),
        };
        this.localService.set(LOCALSTORAGEKEYS.USERPROFILE, merged);
    }
}
